import { useNavigate } from "react-router";
import type { ExtractFilterOptions, GetRecordResponse } from "@generated/import-openapi.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { DatasetResource, PaginationParams } from "@prequel/import-react";
import { useImportContext } from "@prequel/import-react";

import { importClient } from "./sdk/client";

import { useAllPages } from "@/api/pagination";
import { useToast } from "@/providers/ToastProvider";

export type { RecordResource } from "@generated/import-openapi.gen";
export type { DatasetResource } from "@prequel/import-react";

const routes = {
  delete: (id: string) => `/import/datasets/${id}`,
  extract: (id: string) => `/import/datasets/${id}/extract`,
  record: (datasetId: string, recordId: string, asOf?: string) =>
    `/import/datasets/${datasetId}/records/${encodeURIComponent(recordId)}${asOf ? `?as_of=${encodeURIComponent(asOf)}` : ""}`,
};

const keys = {
  all: ["import", "datasets"] as const,
  records: ["import", "datasets", "records"] as const,
};

export function useAllDatasets(sourceId?: string) {
  const { datasets } = useImportContext();

  return useAllPages<DatasetResource>([...keys.all, sourceId ?? ""], (params: PaginationParams) =>
    datasets.list({ ...params, source_id: sourceId }),
  );
}

export function useDatasetRecord(datasetId: string, recordId: string, asOf?: string) {
  return useQuery({
    queryKey: [...keys.records, datasetId, recordId, asOf ?? ""],
    queryFn: () =>
      importClient.get<GetRecordResponse>(routes.record(datasetId, recordId, asOf)).then((data) => data.record),
    retry: false,
  });
}

export function useRunExtract() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, recordIDs }: { id: string; recordIDs?: string[] }) => {
      const filter: ExtractFilterOptions | undefined = recordIDs?.length
        ? { type: "records", records: { record_ids: recordIDs } }
        : undefined;

      return importClient.post(routes.extract(id), filter ? { filter } : undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Extract submitted successfully!" });
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}

export function useDeleteDataset() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => importClient.delete(routes.delete(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      addToast({ kind: "SUCCESS", message: "Dataset deleted successfully!" });
      navigate("/import/datasets");
    },
  });
}
